<template>
  <q-dialog ref="dialogRef" @hide="handleDialogHide">
    <q-card :style="{ minWidth: '600px' }" class="relative-position">
      <q-card-section
        v-if="title"
        class="q-px-2x-md q-py-xl flex justify-between items-center"
      >
        <span class="text-lg text-neutral text-weight-medium">
          {{ title }}
        </span>
        <PBtn
          v-if="closable"
          color="primary"
          flat
          dense
          round
          @click="onCancel"
        >
          <CloseIcon class="icon" />
        </PBtn>
      </q-card-section>
      <q-separator class="fullWidth" />
      <q-card-section class="q-px-2x-md q-pt-xl q-pb-2x-md">
        <q-form greedy @submit.prevent="onSubmit">
          <p v-if="text" :class="textClass">
            <span v-html="text"> </span>

            <span
              v-if="form.input?.validations?.required"
              class="text-error-70"
            >
              *
            </span>
          </p>

          <p v-if="label" :class="labelClass">
            <span :class="labelTextClass">{{ label }}</span>
          </p>

          <PSelectInput
            v-if="form.dropdown"
            v-model="displayName"
            v-bind="form.dropdown"
            :options="unref(form.dropdown.filter.entities)"
            :options-loading="unref(form.dropdown.filter.loading)"
            @filter="form.dropdown.filter.onFilter"
            @filter-abort="form.dropdown.filter.onFilterAbort"
            @virtual-scroll="form.dropdown.filter.onScroll"
            @update:model-value="onUpdateDropDown"
          >
            <template #option="{ itemProps, opt }">
              <q-item v-bind="itemProps">
                <q-item-section>
                  <q-item-label v-if="opt.name">
                    <span class="text-neutral">{{ opt.name }}</span>
                  </q-item-label>
                  <q-item-label v-if="opt.bank_name">
                    <span class="text-neutral"
                      >{{ opt.bank_name }} | A/C {{ opt.account_number }}</span
                    >
                  </q-item-label>
                  <q-item-label v-if="opt.email" caption lines="1">
                    <span class="text-neutral-70">{{ opt.email }}</span>
                  </q-item-label>
                  <q-item-label v-if="opt.ifsc_code" caption lines="1">
                    <span class="text-neutral-70"
                      >IFSC: {{ opt.ifsc_code }}</span
                    >
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
          <OTPGenerationInput
            v-if="form?.otpComponent"
            v-model="otp"
            v-bind="form?.otpComponent"
            :is-send-otp-btn-clicked="isSendOtpBtnClicked"
          />

          <PInput v-if="form.input" v-model="input" v-bind="form.input" />
          <PBtn
            v-if="form.inputCta"
            v-bind="form.inputCta"
            @click="form.inputCta.onClick"
          />

          <div v-if="form.checkbox" class="flex q-pb-md">
            <q-checkbox
              v-model="isSelected"
              v-bind="form.checkbox"
              class="text-neutral"
            />
          </div>
          <span
            v-if="note"
            class="q-mt-sm q-mr-md text-sm text-weight-bold"
            :class="noteClass"
          >
            {{ note }}
          </span>
          <div class="flex justify-end q-pt-sm">
            <PBtn
              v-if="cancellable"
              :fluid="false"
              :label="cancelLabel"
              :style="{ marginInline: '3rem 1rem' }"
              type="cancel"
              outline
              @click="onDialogCancel"
            />
            <PBtn
              v-if="submittable"
              :fluid="false"
              :loading="unref(loading)"
              :label="submitLabel"
              type="submit"
              :disable="unref(disableSubmitBtn)"
            />
          </div>
        </q-form>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { useDialogPluginComponent } from "quasar";
import CloseIcon from "assets/icons/close.svg";
import { unref } from "vue";
import OTPGenerationInput from "src/modules/payments/components/OTPGenerationInput.vue";

const emit = defineEmits([
  ...useDialogPluginComponent.emits,
  "confirm",
  "cancel",
]);

const { dialogRef, onDialogHide, onDialogCancel } = useDialogPluginComponent();

const otp = ref("");

const isSendOtpBtnClicked = ref(false);

const handleIsSendOtpBtnClicked = (value) => {
  isSendOtpBtnClicked.value = value;
};

provide("handleIsSendOtpBtnClicked", handleIsSendOtpBtnClicked);

const disableSubmitBtn = computed(() => {
  if (props.form?.otpComponent) {
    return !unref(isSendOtpBtnClicked);
  }

  return props.form?.input?.required && !unref(input);
});

const input = ref("");

const isSelected = ref(true);

const selectedValue = ref(null);
const displayName = ref("");

watch(
  selectedValue,
  (newValue) => {
    if (newValue === "Others") {
      unref(props.form).input = {
        placeholder: "Enter other reason to reject POD",
        type: "textarea",
        autogrow: true,
        validations: {
          required: true,
          label: "Other Reason",
        },
      };
    }
  },
  { immediate: true },
);

const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  text: {
    type: String,
    default: "",
  },
  label: {
    type: [String, Object],
    default: "",
  },
  note: {
    type: String,
    default: "",
  },
  noteClass: {
    type: String,
    default: "text-neutral",
  },
  cancellable: {
    type: Boolean,
    default: false,
  },
  cancelLabel: {
    type: String,
    default: "No",
  },
  submitLabel: {
    type: String,
    default: "Yes",
  },
  submittable: {
    type: Boolean,
    default: true,
  },
  loading: {
    type: Object,
    default: () => {},
  },
  closable: {
    type: Boolean,
    default: true,
  },
  form: {
    type: Object,
    required: true,
  },
  labelClass: {
    type: String,
    default: "text-center q-ma-xl bg-surface rounded-borders",
  },
  labelTextClass: {
    type: String,
    default: "",
  },
  textClass: {
    type: String,
    default: "text-md text-neutral text-weight-regular",
  },
});

watch(
  props,
  () => {
    if (props.form?.dropdown?.name) {
      displayName.value = props.form?.dropdown?.name;

      selectedValue.value = props.form?.dropdown?._id;
    }
  },
  { deep: true, immediate: true },
);

const onCancel = async () => {
  emit("cancel", input.value, isSelected.value);

  onDialogCancel();
};

const onSubmit = async () => {
  if (props.form.input && props.form.dropdown && props.form.otpComponent) {
    emit("confirm", input.value, selectedValue.value, otp.value);

    return;
  }

  if (props.form.input && props.form.dropdown) {
    emit("confirm", input.value, selectedValue.value);

    return;
  }

  if (props.form.input) {
    emit("confirm", input.value, isSelected.value);
  } else {
    emit("confirm", selectedValue.value);
  }
};

const onUpdateDropDown = (_id) => {
  const entityIdx = unref(props.form.dropdown.filter.entities).findIndex(
    (entity) => entity._id === _id,
  );

  if (entityIdx === -1) {
    selectedValue.value = null;

    return;
  }

  selectedValue.value = unref(props.form.dropdown.filter.entities)[
    entityIdx
  ]._id;
};

const handleDialogHide = () => {
  onDialogHide();
};
</script>
